/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect } from "react";

import { Log } from "../../model/logging";
import { StorageAdapter } from "../../model/storage";
import { LocationUrls } from "../../model/navigation/LocationUrls";
import { createSecretCodeInterceptor } from "../../model/navigation/interceptors/SecretCodeInterceptor";
import type { NavigationEngine } from "../../model/navigation/NavigationEngine";
import { useNavigationService } from "./useNavigationService";

const TAG: string = "useSecretCodeReset";

/** The digit sequence that triggers an app reset. */
const RESET_CODE: string = "571247";

/** Name used to register/remove the interceptor on the engine. */
const INTERCEPTOR_NAME: string = "secretCodeReset";

/**
 * Registers a secret-code interceptor on the navigation engine.
 * When the user enters the code "571247" from any screen, IndexedDB
 * is cleared and the app navigates back to the splash screen.
 *
 * @param engine - The NavigationEngine instance from context.
 */
export function useSecretCodeReset(engine: NavigationEngine): void {
  const { navigateTo } = useNavigationService();

  useEffect((): (() => void) => {
    const interceptor = createSecretCodeInterceptor(RESET_CODE, (): void => {
      Log.i(
        TAG,
        "useSecretCodeReset(): Reset code entered — clearing storage and navigating to splash"
      );

      StorageAdapter.getInstance()
        .clear()
        .then((): void => {
          Log.i(TAG, "useSecretCodeReset(): IndexedDB cleared successfully");
          navigateTo(LocationUrls.SPLASH);
          window.location.reload();
        })
        .catch((error: unknown): void => {
          Log.e(TAG, "useSecretCodeReset(): Failed to clear IndexedDB", error);
          // Navigate to splash even if clear fails
          navigateTo(LocationUrls.SPLASH);
          window.location.reload();
        });
    });

    engine.getInterceptors().use(INTERCEPTOR_NAME, interceptor);

    return (): void => {
      engine.getInterceptors().remove(INTERCEPTOR_NAME);
    };
  }, [engine, navigateTo]);
}
